import { z } from "zod";
import { llmProviderSchema } from "./model-config.js";
import { workspaceNotificationReasonSchema } from "./workspace-notifications.js";
/*
 * The action engine: the producer behind Twin proposals of kind `action`.
 *
 * These contracts cover only producing a proposal: the request the control
 * plane sends the runtime proposer, what the proposer writes back, and the
 * job that carries it. A proposed action is a prompt plus its justification,
 * never an executed one. Deciding it, undoing it and reading it happen
 * through the Twin proposals surface (`twin-proposals.ts`).
 */
export const MAX_ACTIONS_PER_RUN = 5;
export const actionCandidateSchema = z.object({
    title: z.string().trim().min(1).max(120),
    /* The editable artefact: what an accepted action runs as. */
    prompt: z.string().trim().min(1).max(2000),
    /* Shown to the human verbatim; must name the evidence, not restate the title. */
    rationale: z.string().trim().min(1).max(500),
    sourceKeys: z.array(z.string().trim().min(1)).min(1).max(10),
    dedupeKey: z.string().trim().min(1).max(200),
}).strict();
/*
 * A decided action must stay decided. Without this the proposer only ever
 * saw what was still open, so accepting or declining an idea simply cleared
 * the way for the same idea to return under a fresh dedupeKey.
 *
 * An expiry is not a decision: nobody answered it, the clock closed it. The
 * control plane leaves expired proposals out of this list and out of the
 * write-back dedupe, so one may be proposed again on the very next review.
 */
export const DECIDED_ACTION_WINDOW_DAYS = 7;
export const MAX_DECIDED_ACTIONS_IN_REQUEST = 20;
export const DECIDED_ACTION_STATES = ["accepted", "declined"];
export const decidedActionSchema = z.object({
    title: z.string().trim().min(1).max(300),
    dedupeKey: z.string().trim().min(1).max(200),
    state: z.enum(DECIDED_ACTION_STATES),
    decidedAt: z.number().int().nonnegative(),
}).strict();
export const actionFeedDigestItemSchema = z.object({
    key: z.string().trim().min(1),
    summary: z.string().trim().min(1),
    /* Evidence the proposal must be grounded in, not just the headline. */
    preview: z.string().nullable().optional(),
    detail: z.string().nullable().optional(),
    channelName: z.string().nullable().optional(),
    actor: z.string().nullable().optional(),
    actionable: z.boolean().optional(),
    /* False for context rows the user has already seen. */
    unread: z.boolean().optional(),
    occurredAt: z.number().int().optional(),
}).strict();
export const twinActionProposerRequestSchema = z.object({
    jobId: z.string().trim().min(1),
    tenantSlug: z.string().trim().min(1),
    organisationId: z.string().trim().min(1),
    userId: z.string().trim().min(1),
    /* Provider and model come from the fleet's configured routing family. */
    provider: llmProviderSchema,
    model: z.string().trim().min(1).max(200),
    /* How many the control plane will still accept today. */
    remainingToday: z.number().int().min(0),
    /* Wall clock at dispatch, so the proposer can judge how stale a channel is. */
    now: z.number().int().optional(),
    feedDigest: z.array(actionFeedDigestItemSchema).max(60).default([]),
    /* Titles already waiting, so the proposer does not repeat itself. */
    openActionTitles: z.array(z.string()).max(20).default([]),
    /* What the recipient already decided, most recent first. */
    recentlyDecidedActions: z
        .array(decidedActionSchema)
        .max(MAX_DECIDED_ACTIONS_IN_REQUEST)
        .optional(),
    /* Whole-workspace shape, so the proposer can see what has gone quiet. */
    channels: z
        .array(z.object({
        id: z.string(),
        name: z.string(),
        lastActivityDaysAgo: z.number().nullable().optional(),
        eventCount: z.number().optional(),
    }))
        .max(40)
        .default([]),
}).strict();
export const twinActionWriteBackSchema = z.object({
    jobId: z.string().trim().min(1),
    tenantSlug: z.string().trim().min(1),
    organisationId: z.string().trim().min(1),
    userId: z.string().trim().min(1),
    model: z.string().trim().min(1).max(200),
    proposals: z.array(actionCandidateSchema).max(MAX_ACTIONS_PER_RUN),
    /* Why the proposer produced this many, including what it discarded. */
    reason: z.string().trim().max(500).default(""),
}).strict();
export const twinActionWriteBackResponseSchema = z.object({
    jobId: z.string().trim().min(1),
    created: z.number().int().nonnegative(),
    batchId: z.string().trim().min(1).nullable().optional(),
    replay: z.boolean().optional(),
    dropped: z.enum(["background_disabled"]).optional(),
}).strict();
/* Where a background-sensed action came from: the signal batch and the exact
 * signals it cites, so its evidence can be re-authorised later. */
export const actionSignalContextSchema = z.object({
    batchId: z.string().trim().min(1),
    signalIds: z.array(z.string().trim().min(1)).min(1).max(40),
    firstEventSeq: z.number().int().nonnegative(),
    lastEventSeq: z.number().int().nonnegative(),
    maxScore: z.number().int().min(0).max(100),
    policyVersion: z.string().trim().min(1).max(64),
}).strict().refine((context) => context.lastEventSeq >= context.firstEventSeq, { message: "lastEventSeq must not precede firstEventSeq" });
export const actionEvidenceSchema = z.object({
    reason: workspaceNotificationReasonSchema,
    title: z.string().trim().min(1).max(300),
    preview: z.string().trim().max(1_000).nullable(),
    channelName: z.string().trim().max(300).nullable(),
    score: z.number().int().min(0).max(100),
    occurredAt: z.number().int().nonnegative(),
}).strict();
/* Durable column vocabulary, stored in twin_actions.jobs and
 * twin_actions.signal_batches. */
export const actionJobCompletionOutcomeSchema = z.enum([
    "proposals_created",
    "no_proposals",
    "proposer_failed",
]);
